/**
 * QueryFileDropzone Component
 *
 * Drag-and-drop area for uploading YAML query files
 */

import React, { useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import { FileUp } from 'lucide-react';
import { cn } from '@/lib/utils';

interface QueryFileDropzoneProps {
  onFileDrop: (file: File) => void;
  disabled?: boolean;
  className?: string;
}

export const QueryFileDropzone: React.FC<QueryFileDropzoneProps> = ({
  onFileDrop,
  disabled = false,
  className,
}) => {
  const onDrop = useCallback(
    (acceptedFiles: File[]) => {
      if (acceptedFiles.length > 0 && !disabled) {
        onFileDrop(acceptedFiles[0]);
      }
    },
    [onFileDrop, disabled]
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'text/yaml': ['.yml', '.yaml'],
      'application/x-yaml': ['.yml', '.yaml'],
    },
    multiple: false,
    disabled,
    noClick: disabled,
    noKeyboard: disabled,
  });

  return (
    <div
      {...getRootProps()}
      className={cn(
        'relative flex flex-col items-center justify-center p-8 border-2 border-dashed rounded-lg transition-colors cursor-pointer',
        isDragActive
          ? 'border-primary bg-primary/5'
          : 'border-border hover:border-primary/50 hover:bg-muted/50',
        disabled && 'opacity-50 cursor-not-allowed hover:border-border hover:bg-transparent',
        className
      )}
      role="button"
      aria-label="Upload YAML query file"
      tabIndex={disabled ? -1 : 0}
    >
      <input {...getInputProps()} aria-label="File input for query file" />

      <FileUp className="h-8 w-8 text-muted-foreground mb-4" aria-hidden="true" />

      <p className="text-sm text-center mb-2">
        {isDragActive ? (
          <span className="text-primary font-medium">Drop file here...</span>
        ) : (
          <>
            Drag & drop a YAML query file here, or{' '}
            <span className="text-primary font-medium">click to browse</span>
          </>
        )}
      </p>

      <p className="text-xs text-muted-foreground">Supported formats: .yml, .yaml</p>
    </div>
  );
};
